import {create} from 'zustand';

interface UploadItemBase {
  readonly id: string;
  readonly fileName: string;
}

interface UploadingItem extends UploadItemBase {
  readonly status: 'uploading';
  readonly progress: number;
}

interface SuccessfulItem extends UploadItemBase {
  readonly status: 'success';
  readonly progress: 100;
}

interface FailedItem extends UploadItemBase {
  readonly status: 'error';
  readonly progress: number;
  readonly message: string;
}

export type UploadItem = UploadingItem | SuccessfulItem | FailedItem;

interface UploadStore {
  readonly uploads: UploadItem[];
  readonly beginUpload: (uploadId: string, fileName: string) => void;
  readonly updateUploadProgress: (uploadId: string, progress: number) => void;
  readonly completeUpload: (uploadId: string) => void;
  readonly failUpload: (uploadId: string, message: string) => void;
  readonly dismissUpload: (uploadId: string) => void;
  readonly clearUploads: () => void;
}

const assertNever = (value: never): never => {
  throw new Error(`Unexpected upload state: ${JSON.stringify(value)}`);
};

export const useUploadStore = create<UploadStore>((set) => ({
  uploads: [],
  beginUpload: (uploadId, fileName) => {
    set((state) => ({
      uploads: [
        ...state.uploads,
        {id: uploadId, fileName, status: 'uploading', progress: 0},
      ],
    }));
  },
  updateUploadProgress: (uploadId, progress) => {
    const clampedProgress = Math.max(0, Math.min(100, Math.round(progress)));
    set((state) => ({
      uploads: state.uploads.map((upload) => {
        if (upload.id !== uploadId) return upload;

        switch (upload.status) {
          case 'uploading':
            return {...upload, progress: clampedProgress};
          case 'success':
          case 'error':
            return upload;
          default:
            return assertNever(upload);
        }
      }),
    }));
  },
  completeUpload: (uploadId) => {
    set((state) => ({
      uploads: state.uploads.map((upload) => {
        if (upload.id !== uploadId) return upload;

        switch (upload.status) {
          case 'uploading':
            return {...upload, status: 'success', progress: 100};
          case 'success':
          case 'error':
            return upload;
          default:
            return assertNever(upload);
        }
      }),
    }));
  },
  failUpload: (uploadId, message) => {
    set((state) => ({
      uploads: state.uploads.map((upload) => {
        if (upload.id !== uploadId) return upload;

        switch (upload.status) {
          case 'uploading':
          case 'success':
          case 'error':
            return {
              id: upload.id,
              fileName: upload.fileName,
              status: 'error',
              progress: upload.progress,
              message,
            };
          default:
            return assertNever(upload);
        }
      }),
    }));
  },
  dismissUpload: (uploadId) => {
    set((state) => ({
      uploads: state.uploads.filter((upload) => upload.id !== uploadId),
    }));
  },
  clearUploads: () => {
    set({uploads: []});
  },
}));
